export default () => {

    /**
     * syncWithLatestModel
     *
     * Purpose:
     * Ensures a previously saved form model is brought up to date with the latest model structure defined in the application.
     *
     * Why this exists:
     * - Forms can be saved in an "InProgress" state.
     * - Over time, new fields may be added to the model.
     * - Older saved drafts will not contain these new fields.
     * - This function safely injects any missing fields while preserving existing user-entered data.
     *
     * How it works:
     * - Recursively walks the default model structure.
     * - If a field exists in the saved model, it is preserved.
     * - If a field is missing in the saved model, the default value is injected.
     * - Arrays of objects are merged using the first default item as a template.
     *
     * Important:
     * - Does NOT mutate either argument.
     * - Safe to run every time a form loads.
     * - Acts as a backwards-compatibility layer for evolving schemas.
     */
    function syncWithLatestModel(defaults, saved) {

        // If there is no default definition, return whatever was saved.
        // (This protects against undefined schema paths.)
        if (defaults === undefined) return saved;

        // -----------------------------
        // Handle Arrays
        // -----------------------------
        if (Array.isArray(defaults)) {

            // If saved value is not an array, fall back to default array
            if (!Array.isArray(saved)) return defaults;

            // If array contains objects, merge each saved item
            // against the first default item (used as a template).
            if (defaults.length > 0 && typeof defaults[0] === "object") {
                const template = defaults[0];
                return saved.map(item => syncWithLatestModel(template, item));
            }

            // If array contains primitives (strings, numbers, etc),
            // use the saved array as-is.
            return saved;
        }

        // -----------------------------
        // Handle Objects
        // -----------------------------
        if (defaults && typeof defaults === "object") {
            const result = {};

            // Combine keys from both default schema and saved data.
            // This ensures:
            // - New fields get added
            // - Existing saved fields are preserved
            const allKeys = new Set([
                ...Object.keys(defaults),
                ...(saved ? Object.keys(saved) : [])
            ]);

            allKeys.forEach(key => {
                result[key] = syncWithLatestModel(
                    defaults[key],
                    saved ? saved[key] : undefined
                );
            });

            return result;
        }

        // -----------------------------
        // Handle Primitive Values
        // -----------------------------
        // If a saved value exists, use it.
        // Otherwise fall back to the default value.
        return saved !== undefined ? saved : defaults;
    }

    return {
        syncWithLatestModel
    }
}